/**
 * Redux slice for borrower document management
 * Manages various document types required for merchant onboarding and KYC
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface UploadedFile {
  name: string;
  path: string;
}

interface DocBorrowerState {
  business: UploadedFile[];
  share: UploadedFile[];
  balance: UploadedFile[];
  proof: UploadedFile[];
  bank: UploadedFile[];
  salaryProof: UploadedFile[];
  financialStatement: UploadedFile[];
  investmentMandate: UploadedFile[];
  fundManagerLicense: UploadedFile[];
  certificateOfIncorporation: UploadedFile[];
  supportingDocumentsWithSuppliers: UploadedFile[];
  companyConstitution: UploadedFile[];
}

const initialState: DocBorrowerState = {
  business: [],
  share: [],
  balance: [],
  proof: [],
  bank: [],
  salaryProof: [],
  financialStatement: [],
  investmentMandate: [],
  fundManagerLicense: [],
  certificateOfIncorporation: [],
  supportingDocumentsWithSuppliers: [],
  companyConstitution: [],
};

/**
 * Generic reducer factory for document array setters
 * Eliminates duplication for simple array assignment reducers
 */
const createDocumentSetter = <K extends keyof DocBorrowerState>(field: K) => {
  return (state: DocBorrowerState, action: PayloadAction<UploadedFile[]>) => {
    state[field] = action.payload;
  };
};

const docBorrowerSlice = createSlice({
  name: "bordoc",
  initialState,
  reducers: {
    // Business and profile documents
    setBusinessDoc: createDocumentSetter("business"),
    setShareDoc: createDocumentSetter("share"),
    setBalance: createDocumentSetter("balance"),

    // Identity and address documents
    setProofDoc: createDocumentSetter("proof"),
    setBankDoc: createDocumentSetter("bank"),
    setSalaryProofDoc: createDocumentSetter("salaryProof"),

    // Financial documents
    setFinancialStatementDoc: createDocumentSetter("financialStatement"),
    setInvestmentMandateDoc: createDocumentSetter("investmentMandate"),

    // Corporate documents
    setFundManagerLicenseDoc: createDocumentSetter("fundManagerLicense"),
    setCertificateOfIncorporationDoc: createDocumentSetter("certificateOfIncorporation"),
    setSupportingDocumentsWithSuppliersDoc: createDocumentSetter(
      "supportingDocumentsWithSuppliers",
    ),
    setCompanyConstitutionDoc: createDocumentSetter("companyConstitution"),

    /**
     * Bulk update multiple document types at once
     * Useful for hydrating state from API response
     */
    hydrateDocuments: (state, action: PayloadAction<Partial<DocBorrowerState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Clear a specific document type
     */
    clearDocumentType: <K extends keyof DocBorrowerState>(
      state: DocBorrowerState,
      action: PayloadAction<K>,
    ) => {
      state[action.payload] = [];
    },

    /**
     * Reset all documents to initial state
     */
    resetAllDocuments: () => initialState,
  },
});

export const {
  setBusinessDoc,
  setShareDoc,
  setBalance,
  setProofDoc,
  setBankDoc,
  setSalaryProofDoc,
  setFinancialStatementDoc,
  setInvestmentMandateDoc,
  setFundManagerLicenseDoc,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
  setCompanyConstitutionDoc,
  hydrateDocuments,
  clearDocumentType,
  resetAllDocuments,
} = docBorrowerSlice.actions;

export default docBorrowerSlice.reducer;
